import { useEffect, useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import AppLayout from '../../components/layout/AppLayout'
import KpiCard from '../../components/dashboard/KpiCard'
import AbcChart from '../../components/dashboard/AbcChart'
import EstoqueTable from '../../components/dashboard/EstoqueTable'
import EncalheList from '../../components/dashboard/EncalheList'
import Alert from '../../components/ui/Alert'
import { useEstoqueAnalytics, computeKpis } from '../../hooks/useEstoqueAnalytics'
import { isRupturaAcionavel } from '../../lib/replenishment'
import { useToast } from '../../hooks/useToast'
import { useAiInsights } from '../../hooks/useAiInsights'
import { useSync } from '../../contexts/SyncContext'
import { fmtBRL, relativeTime } from '../../lib/format'
import { AI_BTN, AI_LABEL } from '../../lib/statusStyles'
import { kpiMeta } from '../../lib/kpiMeta'
import OperacionalPanel from '../../components/dashboard/OperacionalPanel'
import FinanceiroPanel from '../../components/dashboard/FinanceiroPanel'
import InteligenciaPanel from '../../components/dashboard/InteligenciaPanel'
import ComprasPanel from '../../components/dashboard/ComprasPanel'
import ClientePanel from '../../components/dashboard/ClientePanel'

type PanelId = 'visao-geral' | 'operacional' | 'financeiro' | 'inteligencia' | 'compras' | 'cliente'

const PANELS: { id: PanelId; label: string }[] = [
  { id: 'visao-geral', label: 'Visão Geral' },
  { id: 'operacional', label: 'Operacional' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'inteligencia', label: 'Inteligência' },
  { id: 'compras', label: 'Compras' },
  { id: 'cliente', label: 'Cliente' },
]

function ComingSoon({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center gap-3">
      <span className="text-4xl opacity-30">📊</span>
      <p className="text-ink-faint text-sm">Painel <span className="text-ink font-medium">{label}</span> em breve</p>
    </div>
  )
}

const WINDOW_OPTIONS = [30, 60, 90, 120, 150, 180] as const

const MONTH_ABBR = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

interface MonthOption { value: string; label: string; start: string; end: string; days: number }

/** Últimos `count` meses (mais recente primeiro), com intervalo [1º dia, último dia]. */
function buildMonthOptions(count = 12): MonthOption[] {
  const now = new Date()
  const out: MonthOption[] = []
  for (let i = 0; i < count; i++) {
    const y = now.getFullYear()
    const m = now.getMonth() - i // 0-indexed; pode ficar negativo → Date normaliza
    const first = new Date(y, m, 1)
    const last = new Date(y, m + 1, 0) // dia 0 do mês seguinte = último dia do mês
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    out.push({
      value: `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}`,
      label: `${MONTH_ABBR[first.getMonth() + 1]}/${first.getFullYear()}`,
      start: iso(first),
      end: iso(last),
      days: last.getDate(),
    })
  }
  return out
}

export default function EstoqueDashboardPage() {
  // Janela única que dirige TODOS os painéis. Modo 'dias' (móvel, default) ou 'mes' (calendário).
  const [mode, setMode] = useState<'dias' | 'mes'>('dias')
  const [windowDays, setWindowDays] = useState(180)
  const monthOptions = useMemo(() => buildMonthOptions(12), [])
  const [monthValue, setMonthValue] = useState(() => monthOptions[0]?.value ?? '')
  const selectedMonth = useMemo(
    () => monthOptions.find(m => m.value === monthValue) ?? monthOptions[0],
    [monthOptions, monthValue],
  )
  const isMonth = mode === 'mes'
  const range = isMonth && selectedMonth
    ? { start: selectedMonth.start, end: selectedMonth.end, days: selectedMonth.days }
    : undefined
  // Rótulo do período selecionado, usado nos tooltips/hints (dias móveis ou mês-calendário).
  const periodLabel = isMonth ? (selectedMonth?.label ?? 'mês') : `${windowDays} dias`

  // Mês de referência das Metas de Compras (modo mês = mês escolhido; modo dias = mês atual).
  const metaNow = new Date()
  const metaMonthNumber = isMonth && selectedMonth ? Number(selectedMonth.value.split('-')[1]) : metaNow.getMonth() + 1
  const metaMonthLabel = isMonth && selectedMonth
    ? selectedMonth.label
    : `${MONTH_ABBR[metaNow.getMonth() + 1]}/${metaNow.getFullYear()}`
  const metaMonthKey = isMonth && selectedMonth
    ? selectedMonth.value
    : `${metaNow.getFullYear()}-${String(metaNow.getMonth() + 1).padStart(2, '0')}`
  const todayKey = `${metaNow.getFullYear()}-${String(metaNow.getMonth() + 1).padStart(2, '0')}-${String(metaNow.getDate()).padStart(2, '0')}`

  const windowMonths = Math.max(1, Math.round(windowDays / 30))
  const { rows, inactiveEncalhe, loading, recalculating, error, lastRun, recalcular } = useEstoqueAnalytics(windowDays, range)
  const { notify } = useToast()
  const kpis = useMemo(() => computeKpis(rows, inactiveEncalhe, windowDays), [rows, inactiveEncalhe, windowDays])
  // Metas dos KPIs da Visão Geral (régua única → tom do card + selo). Limiares vêm do texto "analysis".
  const encalheIndexMeta = kpiMeta({ value: kpis.generalEncalheIndex, sentido: 'max', verde: 10, amarelo: 20, format: v => `${v}%` })
  const margemMeta = kpiMeta({ value: kpis.avgMarginPct, sentido: 'min', verde: 30, amarelo: 15, format: v => `${v}%` })
  const { salesSyncing: syncingSales, salesProgress, salesResult, startSalesSync } = useSync()
  const lastNotifiedSalesRef = useRef<typeof salesResult>(null)
  const [activeCard, setActiveCard] = useState<string | null>(null)
  const [activePanel, setActivePanel] = useState<PanelId>('visao-geral')
  const tableRef = useRef<HTMLDivElement>(null)
  const { generate: aiGenerate, loading: aiLoading } = useAiInsights()
  const [aiText, setAiText] = useState<string | null>(null)

  const ENCALHE_DAYS = windowDays
  const encalheCutoffMs = useMemo(() => Date.now() - ENCALHE_DAYS * 24 * 60 * 60 * 1000, [ENCALHE_DAYS])
  const encalheFilter = (r: typeof rows[number]) => {
    if (r.current_stock <= 0) return false
    if (!r.last_sale_at) return false // exige histórico de venda
    return new Date(r.last_sale_at).getTime() < encalheCutoffMs
  }

  // Cada card clicável aplica um filtro (contagem) OU uma ordenação (métrica) na tabela.
  type CardAction = {
    label: string
    filter?: (r: typeof rows[number]) => boolean
    sort?: { key: 'current_stock' | 'coverage_days' | 'revenue_window' | 'margin_pct' | 'value'; dir: 'asc' | 'desc' }
  }
  const CARD_ACTIONS: Record<string, CardAction> = {
    ativos:    { label: 'Todos os SKUs ativos' },
    ruptura:   { label: 'Em ruptura', filter: r => isRupturaAcionavel(r.current_stock, r.kanban_status) },
    excesso:   { label: 'Em excesso', filter: r => r.stock_status === 'excesso' },
    encalhe:   { label: `Encalhe (sem vendas há +${ENCALHE_DAYS}d)`, filter: encalheFilter },
    indice:    { label: `Encalhe (sem vendas há +${ENCALHE_DAYS}d)`, filter: encalheFilter },
    cobertura: { label: 'Ordenado por cobertura (maior primeiro)', sort: { key: 'coverage_days', dir: 'desc' } },
    valor:     { label: 'Ordenado por valor em estoque (maior primeiro)', sort: { key: 'value', dir: 'desc' } },
    receita:   { label: 'Ordenado por receita (maior primeiro)', sort: { key: 'revenue_window', dir: 'desc' } },
    margem:    { label: 'Ordenado por margem (maior primeiro)', sort: { key: 'margin_pct', dir: 'desc' } },
  }

  function selectCard(key: string) {
    const next = activeCard === key ? null : key
    setActiveCard(next)
    if (next) tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const activeAction = activeCard ? CARD_ACTIONS[activeCard] : null
  // "ativos" só limpa/rola — não filtra nem ordena.
  const activeFilter = activeAction?.filter
  const activeSort = activeAction?.sort ?? null
  const activeLabel = activeAction?.filter || activeAction?.sort ? activeAction?.label : undefined

  // Notifica e recalcula indicadores quando o sync de vendas termina (mesmo que o user esteja em outra página)
  useEffect(() => {
    if (!salesResult) return
    if (lastNotifiedSalesRef.current === salesResult) return
    lastNotifiedSalesRef.current = salesResult
    if (salesResult.ok) {
      notify('success', `Vendas sincronizadas: ${salesResult.orders} pedidos, ${salesResult.items} itens.`)
      recalcular()
    } else {
      notify('error', `Sync vendas: ${salesResult.error ?? 'desconhecido'}`)
    }
  }, [salesResult, notify, recalcular])

  async function handleSyncSales() {
    await startSalesSync()
  }

  async function handleRecalc() {
    const ok = await recalcular()
    if (ok) notify('success', 'Indicadores recalculados.')
    else notify('error', 'Falha ao recalcular indicadores.')
  }

  async function handleGenerateAi() {
    // Envia apenas os KPIs já agregados — nenhum dado bruto sai do navegador.
    const r = await aiGenerate('dashboard', {
      periodLabel,
      totalSkus: kpis.totalSkus,
      ruptura: kpis.ruptura,
      excesso: kpis.excesso,
      encalhe: kpis.encalhe,
      inactiveEncalhe: kpis.inactiveEncalhe,
      generalEncalheIndex: kpis.generalEncalheIndex,
      avgCoverage: kpis.avgCoverage,
      totalValue: kpis.totalValue,
      totalRevenue: kpis.totalRevenue,
      totalMargin: kpis.totalMargin,
      avgMarginPct: kpis.avgMarginPct,
      abcBreakdown: kpis.abcBreakdown,
    })
    if (r.ok && r.text) setAiText(r.text)
    else notify('error', r.error ?? 'Falha ao gerar análise.')
  }

  function handleExport() {
    // "Situação" e "Em ruptura" seguem a régua canônica do app (isRupturaAcionavel):
    // ruptura = saldo <= 0 E SEM motivo operacional (consignado/bloqueado/ruptura-fornecedor/standby).
    // Os zerados-com-motivo viram "Zerado (motivo)" — assim a planilha bate com o card do Dashboard.
    const STATUS_LABEL: Record<string, string> = { ruptura: 'Ruptura', excesso: 'Excesso', normal: 'Normal' }
    const data = rows.map(r => {
      const emRuptura = isRupturaAcionavel(r.current_stock, r.kanban_status)
      const situacao = emRuptura
        ? 'Ruptura'
        : r.current_stock <= 0
          ? `Zerado (${r.kanban_status})`
          : (STATUS_LABEL[r.stock_status ?? ''] ?? r.stock_status ?? '')
      return {
        SKU: r.sku,
        Produto: r.name,
        Saldo: r.current_stock,
        'Custo médio': r.cost_avg ?? '',
        'Preço venda': r.sale_price ?? '',
        'Giro/mês': r.monthly_turnover ?? '',
        'Cobertura (dias)': r.coverage_days ?? '',
        'Receita (período)': r.revenue_window ?? '',
        'Margem (R$)': r.margin_value ?? '',
        'Margem (%)': r.margin_pct != null ? r.margin_pct.toFixed(2) : '',
        Curva: r.abc_curve ?? '',
        'Status Kanban': r.kanban_status,
        Situação: situacao,
        'Em ruptura': emRuptura ? 'Sim' : 'Não',
      }
    })
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Estoque Analítico')
    XLSX.writeFile(wb, `estoque_analitico_${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <AppLayout title="Dashboard">
      <div className="flex flex-col gap-5 w-full h-full">
        {/* ── Cabeçalho (full width) ───────────────────────────────── */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-ink">Dashboard</h1>
            <p className="text-sm text-ink-faint">
              Curva ABC, cobertura e situação de cada SKU · Janela: <span className="text-ink-muted">{isMonth ? selectedMonth?.label : `${windowDays} dias`}</span> · cálculo ao vivo (<span className="text-ink-muted">{relativeTime(lastRun)}</span>)
            </p>
            {isMonth && (
              <p className="text-xs text-amber-400/90 mt-1">
                📌 Vendas (receita, CMV, margem, ABC) referem-se ao mês; estoque (capital parado, ruptura, cobertura, encalhe) é snapshot atual.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center w-full md:w-auto">
            <div className="flex rounded-lg border border-line overflow-hidden text-sm" title="Período usado nos painéis">
              <button
                onClick={() => setMode('dias')}
                className={`px-2.5 py-2 ${!isMonth ? 'bg-brand-600 text-white' : 'bg-paper text-ink-muted hover:text-ink'}`}
              >
                Dias
              </button>
              <button
                onClick={() => setMode('mes')}
                className={`px-2.5 py-2 ${isMonth ? 'bg-brand-600 text-white' : 'bg-paper text-ink-muted hover:text-ink'}`}
              >
                Mês
              </button>
            </div>
            <label className="flex items-center gap-1.5 text-sm text-ink-muted">
              {isMonth ? 'Mês:' : 'Janela:'}
              {isMonth ? (
                <select
                  value={monthValue}
                  onChange={e => setMonthValue(e.target.value)}
                  className="bg-paper border border-line rounded-lg px-2.5 py-2 text-ink text-sm focus:outline-none focus:border-brand-500"
                  title="Mês-calendário analisado (recorta as métricas de venda)"
                >
                  {monthOptions.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              ) : (
                <select
                  value={windowDays}
                  onChange={e => setWindowDays(Number(e.target.value))}
                  className="bg-paper border border-line rounded-lg px-2.5 py-2 text-ink text-sm focus:outline-none focus:border-brand-500"
                  title="Período usado em todos os indicadores das 6 abas"
                >
                  {WINDOW_OPTIONS.map(d => (
                    <option key={d} value={d}>{d} dias</option>
                  ))}
                </select>
              )}
            </label>
            <button
              onClick={handleExport}
              disabled={rows.length === 0}
              className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-40 text-ink text-sm font-medium rounded-lg transition-colors"
            >
              Exportar Excel
            </button>
            {activePanel === 'visao-geral' && (
              <button
                onClick={handleGenerateAi}
                disabled={aiLoading || loading || rows.length === 0}
                className={`px-4 py-2 text-sm rounded-lg transition-all flex items-center gap-1.5 ${AI_BTN}`}
                title="Gera um parágrafo de análise dos KPIs via IA (Claude Haiku)"
              >
                {aiLoading ? (
                  <>
                    <span className="w-3 h-3 border border-brand-900/50 border-t-transparent rounded-full animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>✨ Gerar análise (IA)</>
                )}
              </button>
            )}
            <button
              onClick={handleSyncSales}
              disabled={syncingSales || recalculating}
              className="px-4 py-2 bg-sky-700 hover:bg-sky-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              title="Puxa pedidos dos últimos 6 meses do Tiny V3 e recalcula indicadores"
            >
              {syncingSales
                ? salesProgress
                  ? `Sincronizando · ${salesProgress.orders} pedidos / ${salesProgress.items} itens`
                  : 'Sincronizando...'
                : 'Sincronizar Vendas'}
            </button>
            <button
              onClick={handleRecalc}
              disabled={recalculating || syncingSales}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {recalculating ? 'Recalculando...' : 'Recalcular indicadores'}
            </button>
          </div>
        </div>

        {error && <Alert type="error" message={error} />}

        {/* ── Abas de painel — desktop: linha; mobile: grid 3 cols ── */}
        <nav className="hidden md:flex gap-1 border-b border-line -mb-1 overflow-x-auto">
          {PANELS.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePanel(p.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activePanel === p.id
                  ? 'border-brand-600 text-brand-700 dark:text-brand-300'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {p.label}
            </button>
          ))}
        </nav>
        <nav className="md:hidden grid grid-cols-3 gap-1.5">
          {PANELS.map(p => (
            <button
              key={p.id}
              onClick={() => setActivePanel(p.id)}
              className={`h-11 rounded-2xl text-xs font-bold leading-tight px-1 touch-action-manip transition-colors ${
                activePanel === p.id
                  ? 'bg-brand-600 text-white ring-2 ring-ink/50'
                  : 'bg-sunken text-ink-muted'
              }`}
            >
              {p.label}
            </button>
          ))}
        </nav>

        {activePanel === 'operacional' ? (
          loading
            ? <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
            : <OperacionalPanel rows={rows} />
        ) : activePanel === 'financeiro' ? (
          loading
            ? <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
            : <FinanceiroPanel rows={rows} periodLabel={periodLabel} />
        ) : activePanel === 'inteligencia' ? (
          <InteligenciaPanel windowMonths={windowMonths} />
        ) : activePanel === 'compras' ? (
          loading
            ? <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>
            : <ComprasPanel
                rows={rows}
                encalheIndex={kpis.generalEncalheIndex / 100}
                monthNumber={metaMonthNumber}
                monthLabel={metaMonthLabel}
                monthKey={metaMonthKey}
                todayKey={todayKey}
                range={range}
              />
        ) : activePanel === 'cliente' ? (
          <ClientePanel windowMonths={windowMonths} />
        ) : activePanel !== 'visao-geral' ? (
          <ComingSoon label={PANELS.find(p => p.id === activePanel)!.label} />
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {aiText && (
              <div className="relative bg-gradient-to-br from-brand-800/40 to-brand-900 border border-accent/30 rounded-xl p-4 pr-10">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${AI_LABEL}`}>✨ Análise IA</span>
                  <span className="text-[10px] text-ink-faint">· Claude Haiku · resumo dos KPIs do período</span>
                </div>
                <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{aiText}</p>
                <button
                  onClick={() => setAiText(null)}
                  className="absolute top-3 right-3 text-ink-faint hover:text-ink text-sm"
                  title="Fechar análise"
                  aria-label="Fechar análise"
                >
                  ✕
                </button>
              </div>
            )}
            {/* ── KPIs e Chart (full width) ──────────────────────── */}
            {/* Selo de meta só nos cards com alvo (Índice de encalhe, Margem). Contagens/totais
                (SKUs ativos, Em ruptura, Em excesso, Encalhe, Cobertura, Valor, Receita) são informativos → sem selo. */}
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:[&>*]:w-[220px]">
              <KpiCard label="SKUs ativos" value={kpis.totalSkus} hint="clique para listar" onClick={() => selectCard('ativos')} active={activeCard === 'ativos'} analysis="Total de produtos ativos — base das demais métricas. Clique para ver a lista completa." />
              <KpiCard label="Em ruptura" value={kpis.ruptura} tone="danger" hint="clique para listar" onClick={() => selectCard('ruptura')} active={activeCard === 'ruptura'} analysis="SKUs com saldo zerado e sem motivo operacional. Quanto menor, melhor; acima de ~10% do total acende alerta." />
              <KpiCard label="Em excesso" value={kpis.excesso} tone="warning" hint="clique para listar" onClick={() => selectCard('excesso')} active={activeCard === 'excesso'} analysis="SKUs com estoque acima do ideal. Alto = capital preso; revise as compras desses itens." />
              <KpiCard
                label="Encalhe"
                value={kpis.encalhe}
                tone="warning"
                hint="clique para listar"
                onClick={() => selectCard('encalhe')}
                active={activeCard === 'encalhe'}
                analysis={`Com saldo e sem venda há +${ENCALHE_DAYS} dias (snapshot atual). Candidatos a promoção ou liquidação para liberar capital.`}
              />
              <KpiCard
                label="Índice geral de encalhe"
                value={`${kpis.generalEncalheIndex.toFixed(1)}%`}
                tone={encalheIndexMeta.tone}
                meta={{ status: encalheIndexMeta.status, label: encalheIndexMeta.metaLabel }}
                hint={`${kpis.encalhe} ativos + ${kpis.inactiveEncalhe} inativos`}
                onClick={() => selectCard('indice')}
                active={activeCard === 'indice'}
                analysis="% de SKUs encalhados (ativos + inativos). <10% bom · 10–20% atenção · >20% crítico."
              />
              <KpiCard label="Cobertura média" value={`${kpis.avgCoverage}d`} tone="info" hint="clique para ordenar" onClick={() => selectCard('cobertura')} active={activeCard === 'cobertura'} analysis="Dias médios que o estoque cobre. Muito baixo = risco de ruptura; muito alto = excesso." />
              <KpiCard label="Valor em estoque" value={fmtBRL(kpis.totalValue)} tone="success" hint="clique para ordenar" onClick={() => selectCard('valor')} active={activeCard === 'valor'} analysis="Capital total imobilizado (a custo). Alta sem aumento de vendas indica excesso de estoque." />
              <KpiCard
                label="Receita no período"
                value={fmtBRL(kpis.totalRevenue)}
                tone="info"
                hint="clique para ordenar"
                onClick={() => selectCard('receita')}
                active={activeCard === 'receita'}
                analysis={`Vendas no período selecionado (${periodLabel}). Base para giro e margem; acompanhe a tendência.`}
              />
              <KpiCard
                label="Margem de contribuição"
                value={kpis.avgMarginPct != null ? `${kpis.avgMarginPct.toFixed(1)}%` : '—'}
                tone={margemMeta.tone}
                meta={{ status: margemMeta.status, label: margemMeta.metaLabel }}
                hint="clique para ordenar"
                onClick={() => selectCard('margem')}
                active={activeCard === 'margem'}
                analysis="% que sobra após o custo da mercadoria. ≥30% bom · 15–30% atenção · <15% baixo."
              />
            </div>

            <AbcChart breakdown={kpis.abcBreakdown} />

            {/* ── Tabela + ENCALHE alinhadas, ocupam altura restante do viewport ── */}
            <div className="flex gap-5 items-stretch flex-1 min-h-0">
              <div ref={tableRef} className="flex-1 min-w-0 flex flex-col">
                <EstoqueTable
                  rows={rows}
                  extraFilter={activeFilter}
                  extraFilterLabel={activeLabel}
                  onClearExtraFilter={activeCard ? () => setActiveCard(null) : undefined}
                  sortOverride={activeSort}
                />
              </div>
              <aside className="hidden xl:block w-[24rem] 2xl:w-[28rem] shrink-0">
                <EncalheList rows={rows} cutoffDays={ENCALHE_DAYS} />
              </aside>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}
